import { type HelpScreen, isBackofficeScreen, isProjectScreen } from "@esi/shared";
import {
  ACCOUNT_PATH,
  adminSettingsPath,
  BACKOFFICE_PATH_ROOT,
  type BackofficeSection,
  CONVERSATIONS_PATH,
  HOME_PATH,
  PROJECTS_PATH,
  type ProjectSettingsGroup,
  type ProjectTab,
  projectHomePath,
  projectSettingsPath,
  type SettingsGroup,
  wikiPath,
} from "../router.tsx";

/**
 * Where an "ESI help" chip leads (openspec change `agent-self-knowledge-help`, design D7): the
 * page's screen, resolved here from its route name (never a URL from the answer), for the
 * conversation's project and the viewer's roles. A project screen needs the conversation's
 * project; a backoffice screen needs an admin role. Otherwise there is no link, and why.
 */
export type HelpTarget =
  | { href: string }
  | { href: null; reason: "noScreen" | "noProject" | "adminOnly" };

export function helpTarget(
  screen: HelpScreen | undefined,
  scope: { projectId: string | null; admin: boolean },
): HelpTarget {
  if (!screen) {
    return { href: null, reason: "noScreen" };
  }
  if (isBackofficeScreen(screen) && !scope.admin) {
    return { href: null, reason: "adminOnly" };
  }
  if (isProjectScreen(screen) && !scope.projectId) {
    return { href: null, reason: "noProject" };
  }
  return { href: helpScreenPath(screen, scope.projectId ?? "") };
}

/** The path of a screen; `projectId` fills a project screen. */
export function helpScreenPath(screen: HelpScreen, projectId: string): string {
  const [area, sub] = screen.split(".") as [string, string | undefined];
  switch (area) {
    case "home":
      return HOME_PATH;
    case "projects":
      return PROJECTS_PATH;
    case "conversations":
      return CONVERSATIONS_PATH;
    case "tasks":
      return "/tasks";
    case "knowledge":
      return "/knowledge";
    case "account":
      return ACCOUNT_PATH;
    case "projectHome":
      return sub === "wiki" ? wikiPath(projectId) : projectHomePath(projectId, sub as ProjectTab);
    case "projectSettings":
      return projectSettingsPath(projectId, sub as ProjectSettingsGroup);
    case "backofficeSettings":
      return adminSettingsPath(sub as SettingsGroup);
    default:
      return `${BACKOFFICE_PATH_ROOT}/${sub as BackofficeSection}`;
  }
}
